// Shared graph primitive types and Zod schemas (ARCHITECTURE.md §4.1).
//
// These are the foundational vertex/edge definitions used across every
// downstream module. Zod schemas provide runtime validation at trust
// boundaries (cache deserialization, plugin input, CLI args); the inferred
// TypeScript types are the compile-time source of truth.

import { z } from "zod";

import { EvidenceSchema } from "./evidence/types.js";

// ---------------------------------------------------------------------------
// Node kinds
// ---------------------------------------------------------------------------

export const NODE_KINDS = [
  "file",
  "symbol",
  "module",
  "package",
  "artifact",
] as const;

export const NodeKindSchema = z.enum(NODE_KINDS);

export type NodeKind = z.infer<typeof NodeKindSchema>;

// ---------------------------------------------------------------------------
// Edge kinds & classification
// ---------------------------------------------------------------------------

export const EDGE_KINDS = [
  "imports",
  "exports",
  "calls",
  "type_reference",
  "re_exports",
  "depends_on",
  "contains",
  "boundary_crossing",
] as const;

export const EdgeKindSchema = z.enum(EDGE_KINDS);

export type EdgeKind = z.infer<typeof EdgeKindSchema>;

export const EDGE_CLASSIFICATIONS = ["deterministic", "candidate"] as const;

export const EdgeClassificationSchema = z.enum(EDGE_CLASSIFICATIONS);

export type EdgeClassification = z.infer<typeof EdgeClassificationSchema>;

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

// Evidence is the edge provenance model defined in src/evidence/types.ts
// (ARCHITECTURE.md §4.2). GraphEdgeSchema references EvidenceSchema below; the
// public Evidence types are exported from the evidence module barrel.

// ---------------------------------------------------------------------------
// GraphNode
// ---------------------------------------------------------------------------

export const GraphNodeSchema = z.object({
  id: z.string(),
  kind: NodeKindSchema,
  metadata: z.record(z.string(), z.unknown()),
});

export type GraphNode = z.infer<typeof GraphNodeSchema>;

// ---------------------------------------------------------------------------
// GraphEdge
// ---------------------------------------------------------------------------

export const GraphEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  kind: EdgeKindSchema,
  classification: EdgeClassificationSchema,
  evidence: z.array(EvidenceSchema),
  /**
   * Deterministic relevance score derived from `evidence` (Issue #21). Set by
   * the dependency graph builder; optional so hand-built and legacy edges (and
   * cache entries predating the field) remain valid.
   */
  relevanceScore: z.number().optional(),
});

export type GraphEdge = z.infer<typeof GraphEdgeSchema>;
